import React, { useState } from 'react';
import { Info } from 'lucide-react';

/**
 * SampleTableViewPage - Development Reference Page
 *
 * This page demonstrates the standard module layout with labeled UI regions.
 * Use this as a reference when building new modules or discussing UI changes.
 *
 * Layout Regions:
 * ┌─────────────────────────────────────────────────────────────┐
 * │  A: MODULE HEADER                                           │
 * │  - View toggle (List/Datasheet)                             │
 * │  - Search bar                                               │
 * │  - Filter indicators                                        │
 * │  - CRUD buttons                                             │
 * │  - Export button                                            │
 * ├──────────────┬──────────────────────────────────────────────┤
 * │  B: LIST     │  C: DETAIL PANEL                             │
 * │     PANEL    │  ┌─────────────────────────────────────────┐ │
 * │              │  │ C1: Section Header                      │ │
 * │  - Items     │  ├─────────────────────────────────────────┤ │
 * │  - Primary   │  │ C2: 24-Column Form Grid                 │ │
 * │    text      │  │                                         │ │
 * │  - Secondary │  │  [col-span-2] [col-span-4] [col-span-6] │ │
 * │    text      │  │  [col-span-8] [col-span-12][col-span-24]│ │
 * │              │  │                                         │ │
 * │              │  └─────────────────────────────────────────┘ │
 * └──────────────┴──────────────────────────────────────────────┘
 */

const SampleTableViewPage = () => {
  const [_selectedItem] = useState({ id: 1, name: 'Sample Item' }); // eslint-disable-line no-unused-vars

  return (
    <div className="module-container">
      {/* ═══════════════════════════════════════════════════════════════
          REGION A: MODULE HEADER
          - Contains view toggle, search, filters, CRUD buttons, export
          ═══════════════════════════════════════════════════════════════ */}
      <div className="dev-region dev-region-header">
        <div className="dev-region-label">A: MODULE HEADER</div>
        <div className="module-header">
          <div className="module-header-left">
            <div className="view-toggle">
              <button className="view-toggle-btn active">List</button>
              <button className="view-toggle-btn">Datasheet</button>
            </div>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search..."
                className="search-input"
                disabled
              />
            </div>
            <span className="record-count">5 of 100 records</span>
          </div>
          <div className="module-header-right">
            <button className="btn btn-secondary" disabled>+ Add</button>
            <button className="btn btn-secondary" disabled>Edit</button>
            <button className="btn btn-secondary" disabled>Export</button>
          </div>
        </div>
      </div>

      {/* Main content area with List and Detail panels */}
      <div className="list-view">

        {/* ═══════════════════════════════════════════════════════════════
            REGION B: LIST PANEL
            - Scrollable list of items
            - Primary text (bold) + secondary text (muted)
            - Click to select and show in detail panel
            ═══════════════════════════════════════════════════════════════ */}
        <div className="dev-region dev-region-list">
          <div className="dev-region-label">B: LIST PANEL</div>
          <div className="list-panel">
            <div className="list-panel-content">
              {[1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  className={`list-item ${i === 1 ? 'selected' : ''}`}
                >
                  <div className="list-item-primary">Sample Item {i}</div>
                  <div className="list-item-secondary">Secondary info line</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            REGION C: DETAIL PANEL
            - Shows selected item details
            - Contains section headers (C1) and form grids (C2)
            ═══════════════════════════════════════════════════════════════ */}
        <div className="dev-region dev-region-detail">
          <div className="dev-region-label">C: DETAIL PANEL</div>
          <div className="detail-panel">

            {/* ─────────────────────────────────────────────────────────────
                REGION C1: SECTION HEADER
                - Separates logical groups of fields
                ───────────────────────────────────────────────────────────── */}
            <div className="dev-subregion">
              <div className="dev-subregion-label">C1: Section Header</div>
              <div className="detail-section-header">General Information</div>
            </div>

            {/* ─────────────────────────────────────────────────────────────
                REGION C2: 24-COLUMN FORM GRID
                - Uses CSS Grid with 24 columns for finer granularity
                - Fields span columns: col-span-1 through col-span-24
                ───────────────────────────────────────────────────────────── */}
            <div className="dev-subregion">
              <div className="dev-subregion-label">C2: 24-Column Form Grid</div>

              <div className="detail-grid-24">
                {/* Row 1: Demonstrate various column spans */}
                <div className="form-group col-span-2">
                  <label>col-span-2</label>
                  <input type="text" value="2" readOnly className="form-input" />
                </div>
                <div className="form-group col-span-4">
                  <label>col-span-4</label>
                  <input type="text" value="4 cols" readOnly className="form-input" />
                </div>
                <div className="form-group col-span-6">
                  <label>col-span-6</label>
                  <input type="text" value="6 columns" readOnly className="form-input" />
                </div>
                <div className="form-group col-span-12">
                  <label>col-span-12 (half width)</label>
                  <input type="text" value="12 columns - half the grid" readOnly className="form-input" />
                </div>

                {/* Row 2: Common patterns - thirds */}
                <div className="form-group col-span-8">
                  <label>col-span-8 (third width)</label>
                  <input type="text" value="8 columns" readOnly className="form-input" />
                </div>
                <div className="form-group col-span-8">
                  <label>col-span-8</label>
                  <input type="text" value="8 columns" readOnly className="form-input" />
                </div>
                <div className="form-group col-span-8">
                  <label>col-span-8</label>
                  <input type="text" value="8 columns" readOnly className="form-input" />
                </div>

                {/* Row 3: Full width */}
                <div className="form-group col-span-24">
                  <label>col-span-24 (full width)</label>
                  <input type="text" value="Full width field - 24 columns" readOnly className="form-input" />
                </div>

                {/* Row 4: Textarea example */}
                <div className="form-group col-span-24">
                  <label>col-span-24 Textarea</label>
                  <textarea
                    value="Multi-line text fields also use the 24-column grid system"
                    readOnly
                    className="form-input"
                    rows={3}
                  />
                </div>

                {/* Row 5: FK Link example */}
                <div className="form-group col-span-12">
                  <label>FK Link Field (clickable)</label>
                  <input
                    type="text"
                    value="Click to navigate →"
                    readOnly
                    className="form-input form-input-link"
                    title="FK links navigate to related records"
                  />
                </div>
                <div className="form-group col-span-12">
                  <label>Regular Field</label>
                  <input type="text" value="Non-clickable" readOnly className="form-input" />
                </div>
              </div>
            </div>

            {/* Additional section example */}
            <div className="dev-subregion stui-mt-lg">
              <div className="dev-subregion-label">C1: Another Section</div>
              <div className="detail-section-header">Additional Details</div>
            </div>

            <div className="detail-grid-24">
              <div className="form-group col-span-6">
                <label>Field A</label>
                <input type="text" value="Value A" readOnly className="form-input" />
              </div>
              <div className="form-group col-span-6">
                <label>Field B</label>
                <input type="text" value="Value B" readOnly className="form-input" />
              </div>
              <div className="form-group col-span-6">
                <label>Field C</label>
                <input type="text" value="Value C" readOnly className="form-input" />
              </div>
              <div className="form-group col-span-6">
                <label>Field D</label>
                <input type="text" value="Value D" readOnly className="form-input" />
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="dev-legend">
        <div className="dev-legend-title">
          <Info size={16} />
          <span>Development Reference Guide</span>
        </div>
        <div className="dev-legend-content">
          <div className="dev-legend-item">
            <strong>A: Module Header</strong> - View toggle, search, filters, CRUD buttons, export
          </div>
          <div className="dev-legend-item">
            <strong>B: List Panel</strong> - Scrollable item list with primary/secondary text
          </div>
          <div className="dev-legend-item">
            <strong>C: Detail Panel</strong> - Selected item details with form fields
          </div>
          <div className="dev-legend-item">
            <strong>C1: Section Header</strong> - Groups related fields visually
          </div>
          <div className="dev-legend-item">
            <strong>C2: 24-Column Grid</strong> - CSS Grid layout (col-span-1 to col-span-24)
          </div>
          <div className="dev-legend-item">
            <strong>form-input-link</strong> - Clickable FK field that navigates to related record
          </div>
        </div>
      </div>
    </div>
  );
};

export default SampleTableViewPage;
